// src/tattoo/DecalEditor.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useLayoutEffect,
} from "react";
import { useSnapshot } from "valtio";
import {
  tattoo,
  setLayerTransform,
  guideKeyFor,
  pushTransformUndo,
  canUndoTransform,
  undoTransform,
} from "./state";
import "./DPad.css";
import { setRenderQuality } from "./state"; // adjust path
import { RotateCcw, VectorSquare, Scaling, Minus, Plus } from "lucide-react";

// --- helpers -------------------------------------------------
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

let _wmLogoPromise = null;

function loadWmLogo(url) {
  if (!_wmLogoPromise) {
    _wmLogoPromise = new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
  }
  return _wmLogoPromise;
}

// small positional step as a fraction of the current UV rect
const NUDGE_STEP_FRACTION = 0.0005;

const SCALE_MIN = 0.02;
const SCALE_MAX = 1.0;
function sliderToScale(s) {
  const r = SCALE_MAX / SCALE_MIN;
  return SCALE_MIN * Math.pow(r, s);
}
function scaleToSlider(scale) {
  const r = SCALE_MAX / SCALE_MIN;

  // guard against bad values so the slider never gets NaN
  const safeScale =
    typeof scale === "number" && isFinite(scale) && scale > 0
      ? scale
      : Math.sqrt(SCALE_MIN * SCALE_MAX); // midpoint as fallback

  return Math.log(safeScale / SCALE_MIN) / Math.log(r);
}

async function makeWatermarkedThumbWithLogo(srcUrl, logoUrl) {
  // load source image
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = srcUrl;
  await img.decode();

  const size = 512;
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");

  // ✅ transparent background (prevents black)
  ctx.clearRect(0, 0, size, size);

  // ✅ contain (no crop)
  const imgW = img.naturalWidth || img.width || 1;
  const imgH = img.naturalHeight || img.height || 1;

  const s = Math.min(size / imgW, size / imgH);
  const dw = imgW * s;
  const dh = imgH * s;
  const dx = (size - dw) / 2;
  const dy = (size - dh) / 2;

  ctx.drawImage(img, dx, dy, dw, dh);

  // watermark logo
  const logo = await loadWmLogo(logoUrl);

  ctx.save();
  ctx.globalAlpha = 0.22;

  // centered, larger watermark
  const targetW = Math.round(size * 0.65);
  const aspect =
    (logo.naturalHeight || logo.height) / (logo.naturalWidth || logo.width);
  const targetH = Math.round(targetW * aspect);

  ctx.drawImage(
    logo,
    (size - targetW) / 2,
    (size - targetH) / 2,
    targetW,
    targetH
  );

  ctx.restore();

  return c.toDataURL("image/png");
}

function setGlobalTTQuality(q) {
  window.__tt_quality = q;
  window.dispatchEvent(new CustomEvent("tt:quality", { detail: q }));
}

// 🔐 make sure scales are always valid numbers
function safeScale(s) {
  if (typeof s !== "number" || !isFinite(s)) return 0.5;
  return Math.max(SCALE_MIN, Math.min(SCALE_MAX, s));
}

// image intrinsic size
const useImageDims = (src) => {
  const [dims, setDims] = useState(null); // {iw, ih}
  useEffect(() => {
    if (!src) {
      setDims(null);
      return;
    }
    const img = new Image();
    img.onload = () =>
      setDims({
        iw: img.naturalWidth || img.width,
        ih: img.naturalHeight || img.height,
      });
    img.onerror = () => setDims(null);
    img.src = src;
  }, [src]);
  return dims;
};

// stage pixel size
function useStageSize(ref) {
  const [sz, setSz] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSz({ w: r.width, h: r.height });
    });
    ro.observe(el);
    const r = el.getBoundingClientRect();
    setSz({ w: r.width, h: r.height });
    return () => ro.disconnect();
  }, [ref]);
  return sz;
}

// object-fit: cover rect inside a box
function coverRect(boxW, boxH, iw, ih) {
  const s = Math.max(boxW / iw, boxH / ih);
  const dw = iw * s,
    dh = ih * s;
  const dx = (boxW - dw) * 0.5;
  const dy = (boxH - dh) * 0.5;
  return { dx, dy, dw, dh };
}

let __ttWmLogoImg = null;

// --- component -----------------------------------------------
export default function DecalEditor({ partKey = null }) {
  const snap = useSnapshot(tattoo);

 


  const layer = useMemo(
    () => (snap.layers || []).find((x) => x.id === snap.editingLayerId),
    [snap.layers, snap.editingLayerId]
  );
  if (!layer) return null;

  const hasImage = typeof layer.url === "string" && layer.url.trim() !== "";

  const url = layer?.url || "";
  const isFlash = layer?.source === "flash";
  const isPaint = (layer?.name || "").toLowerCase() === "paint"; // ✅ simplest
  const showWm = isFlash && !isPaint; // ✅ allow data: for flash

  // pick the proper guide/rect for current sex + view
  const key = guideKeyFor(snap.sex, snap.viewMode, partKey || "torso");


 



  const guideSrc =
    snap.uvGuides?.[key] ||
    (key === "fullbody_female"
      ? "/tattoo/uv/female_full_uv.png"
      : key === "fullbody_male"
      ? "/tattoo/uv/male_full_uv.png"
      : "/tattoo/uv/torso_uv.png");

  const uvRect = snap.uvRects?.[key] || { x: 0, y: 0, w: 1, h: 1 };

  const guideDims = useImageDims(guideSrc); // {iw, ih}

  // mapping editor<->UV
  const editorToUV = useCallback(
    (x, y) => ({
      u: clamp01(uvRect.x + clamp01(x) * uvRect.w),
      v: clamp01(uvRect.y + clamp01(y) * uvRect.h),
    }),
    [uvRect]
  );
  const uvToEditor = useCallback(
    (u, v) => ({
      x: clamp01((clamp01(u) - uvRect.x) / Math.max(1e-6, uvRect.w)),
      y: clamp01((clamp01(v) - uvRect.y) / Math.max(1e-6, uvRect.h)),
    }),
    [uvRect]
  );

  // normalized transform in state: support scaleX/scaleY, fall back to legacy `scale`
  const rawTransform = layer.transform || {
    tx: 0.5,
    ty: 0.5,
    scale: 0.5,
    rot: 0,
  };

  const baseScale = rawTransform.scale ?? 0.5;
  const sx = rawTransform.scaleX ?? baseScale;
  const sy = rawTransform.scaleY ?? baseScale;

  const T = {
    ...rawTransform,
    scaleX: sx,
    scaleY: sy,
  };

  // where to show the decal (UV -> editor space)
  const posEd = uvToEditor(T.tx ?? 0.5, T.ty ?? 0.5);

  // UI values (robust)
  const clampedSx = safeScale(T.scaleX);
  const clampedSy = safeScale(T.scaleY);

  // a "main" uniform scale for the locked slider + label
  const uniformScale = (clampedSx + clampedSy) * 0.5;
  const uniformSliderVal = scaleToSlider(uniformScale);

  // slider values for per-axis scaling
  const scaleXSliderVal = scaleToSlider(clampedSx);
  const scaleYSliderVal = scaleToSlider(clampedSy);

  const rotDeg = Math.round(toDeg(T.rot || 0));

  // zoom & pan
  const stageRef = useRef(null);
  const contentRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  // lock/unlock proportionate scaling
  const [lockAspect, setLockAspect] = useState(true);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // 🔹 for corner gizmo drag state
  const gizmoDragRef = useRef(null);
  const dragOffsetRef = useRef(null);

  const shouldWatermark = layer?.source === "flash";

  // state hooks
  const [watermarkedPreviewUrl, setWatermarkedPreviewUrl] = useState(null);

  // 👉 ADD THIS USEEFFECT RIGHT HERE
  useEffect(() => {
    let cancelled = false;

    async function buildPreview() {
      if (!layer?.url) {
        setWatermarkedPreviewUrl(null);
        return;
      }

      const isFlash = layer.source === "flash";

      if (!isFlash) {
        setWatermarkedPreviewUrl(null);
        return;
      }

      try {
        const wm = await makeWatermarkedThumbWithLogo(
          layer.url,
          "/tt-watermark.png"
        );

        if (!cancelled) {
          setWatermarkedPreviewUrl(wm);
        }
      } catch (e) {
        console.warn("[wm] preview build failed", e);
        if (!cancelled) setWatermarkedPreviewUrl(null);
      }
    }

    buildPreview();

    return () => {
      cancelled = true;
    };
  }, [layer?.url, layer?.source]);

  const zoomTo = (next, pivot) => {
    next = Math.max(1, Math.min(8, next));
    if (!stageRef.current || !contentRef.current) {
      setZoom(next);
      setPan({ x: 0, y: 0 });
      return;
    }
    const rect = stageRef.current.getBoundingClientRect();
    const cx = (pivot?.x ?? rect.left + rect.width / 2) - rect.left;
    const cy = (pivot?.y ?? rect.top + rect.height / 2) - rect.top;
    const sx = (cx - pan.x) / zoom;
    const sy = (cy - pan.y) / zoom;
    const newPan = { x: cx - sx * next, y: cy - sy * next };
    setZoom(next);
    setPan(newPan);
  };
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -1 : 1;
    zoomTo(zoom * (1 + delta * 0.2), { x: e.clientX, y: e.clientY });
  };
  const startPan = (e) => {
    if (zoom <= 1) return;
    const rect = stageRef.current.getBoundingClientRect();
    const drag = {
      startX: e.clientX,
      startY: e.clientY,
      panX: pan.x,
      panY: pan.y,
      minX: rect.width * (1 - zoom),
      minY: rect.height * (1 - zoom),
      maxX: 0,
      maxY: 0,
    };
    document.body.style.cursor = "grabbing";
    const onMove = (ev) => {
      const nx = Math.max(
        drag.minX,
        Math.min(drag.maxX, drag.panX + (ev.clientX - drag.startX))
      );
      const ny = Math.max(
        drag.minY,
        Math.min(drag.maxY, drag.panY + (ev.clientY - drag.startY))
      );
      setPan({ x: nx, y: ny });
      ev.preventDefault();
    };
    const onUp = () => {
      document.body.style.cursor = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { once: true });
    e.preventDefault();
  };
  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // compute a single cover box we’ll use for BOTH guide and decal
  const stageSize = useStageSize(stageRef);
  const cover = useMemo(() => {
    if (!guideDims || !stageSize.w || !stageSize.h) return null;
    return coverRect(stageSize.w, stageSize.h, guideDims.iw, guideDims.ih); // {dx,dy,dw,dh}
  }, [guideDims, stageSize]);

  // 🔹 frame size in pixels, based on current scale and cover box
  const frameWidth = cover ? cover.dw * clampedSx : 0;
  const frameHeight = cover ? cover.dh * clampedSy : 0;

  const [wmPreviewUrl, setWmPreviewUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
  
    async function run() {
      const url = layer?.url;
      const isFlash = layer?.source === "flash";
  
      if (!url || !isFlash) {
        setWatermarkedPreviewUrl(null);
        return;
      }
  
      try {
        const wm = await makeWatermarkedThumbWithLogo(url, "/tt-watermark.png");
        if (!cancelled) setWatermarkedPreviewUrl(wm);
      } catch (e) {
        console.warn("[wm] preview build failed", e);
        if (!cancelled) setWatermarkedPreviewUrl(null);
      }
    }
  
    run();
    return () => {
      cancelled = true;
    };
  }, [layer?.url, layer?.source]);
  
  const srcToShow =
    layer?.source === "flash" && watermarkedPreviewUrl
      ? watermarkedPreviewUrl
      : layer?.url;

  // --- D-Pad control (3x2 layout) ---------------------------
  function DPad({ onUp, onDown, onLeft, onRight, disabled }) {
    const baseCell = {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };

    const btnBase = {
      width: "100%",
      height: "100%",
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: disabled ? "default" : "pointer",
    };

    const triBase = {
      width: 0,
      height: 0,
      borderStyle: "solid",
    };
    const dpadOuterStyle = {
      flex: 1,
      display: "flex",
      justifyContent: "center",
    };

    const dpadStyle = {
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gridTemplateRows: "1fr 1fr",
      gap: 8,
      padding: 14,
      borderRadius: 10,
      border: "1px solid #3a3a3a",
      background: "#2a2a2a",
      width: "100%",
      maxWidth: 320,
    };

    const dpadCellStyle = {
      borderRadius: 10,
      border: "1px solid #4a4a4a",
      background: "rgba(0,0,0,0.25)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: hasImage ? "pointer" : "default",
      padding: 0,
    };

    const dpadArrowStyle = {
      fontSize: 18,
      lineHeight: 1,
      pointerEvents: "none",
    };

    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          width: 140, // tweak to taste
          height: 70, // tweak to taste
          background: "#333",
          border: "1px solid #777",
          boxSizing: "border-box",
        }}
      >
        {/* empty TL */}
        <div style={baseCell} />

        {/* UP */}
        <div
          style={{
            ...baseCell,
            borderBottom: "1px solid #777",
          }}
        >
          <button
            type="button"
            style={btnBase}
            onClick={disabled ? undefined : onUp}
            disabled={disabled}
            title="Nudge up"
          >
            <span
              style={{
                ...triBase,
                borderWidth: "0 10px 10px 10px",
                borderColor: "transparent transparent #e3e3e3 transparent",
              }}
            />
          </button>
        </div>

        {/* empty TR */}
        <div style={baseCell} />

        {/* LEFT */}
        <div
          style={{
            ...baseCell,
            borderRight: "1px solid #777",
          }}
        >
          <button
            type="button"
            style={btnBase}
            onClick={disabled ? undefined : onLeft}
            disabled={disabled}
            title="Nudge left"
          >
            <span
              style={{
                ...triBase,
                borderWidth: "10px 10px 10px 0",
                borderColor: "transparent #e3e3e3 transparent transparent",
              }}
            />
          </button>
        </div>

        {/* DOWN */}
        <div
          style={{
            ...baseCell,
            borderTop: "1px solid #777",
          }}
        >
          <button
            type="button"
            style={btnBase}
            onClick={disabled ? undefined : onDown}
            disabled={disabled}
            title="Nudge down"
          >
            <span
              style={{
                ...triBase,
                borderWidth: "10px 10px 0 10px",
                borderColor: "#e3e3e3 transparent transparent transparent",
              }}
            />
          </button>
        </div>

        {/* RIGHT */}
        <div
          style={{
            ...baseCell,
            borderLeft: "1px solid #777",
          }}
        >
          <button
            type="button"
            style={btnBase}
            onClick={disabled ? undefined : onRight}
            disabled={disabled}
            title="Nudge right"
          >
            <span
              style={{
                ...triBase,
                borderWidth: "10px 0 10px 10px",
                borderColor: "transparent transparent transparent #e3e3e3",
              }}
            />
          </button>
        </div>
      </div>
    );
  }

  // drag logic (maps pointer -> cover space -> uvRect)
  const draggingRef = useRef(false);
  const rafId = useRef(0);
  const pending = useRef(null);
  const lastSent = useRef({ u: T.tx ?? 0.5, v: T.ty ?? 0.5 });

  // 🔹 new: distinguish drag modes + rotation state
  const modeRef = useRef(null); // "move" | "rotate" | null

  const rotateDragRef = useRef({ offset: 0 });

  // --- UNDO plumbing ----------------------------------------
  const undoPushedRef = React.useRef(false);
  const ensureUndoStart = React.useCallback(() => {
    if (!undoPushedRef.current) {
      const prev = layer.transform || { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 };
      try {
        pushTransformUndo(layer.id, prev);
      } catch {}
      undoPushedRef.current = true;
    }
  }, [layer.id, layer.transform]);
  const endInteraction = React.useCallback(() => {
    undoPushedRef.current = false;
  }, []);
  useEffect(() => {
    undoPushedRef.current = false;
  }, [layer.id]);

  // reactive Undo availability
  const [undoReady, setUndoReady] = useState(false);
  useEffect(() => {
    setUndoReady(canUndoTransform(layer.id));
  }, [layer.id, T.tx, T.ty, T.scaleX, T.scaleY, T.rot, snap.editingLayerId]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const url = layer?.url || "";

      const isDataUrl = typeof url === "string" && url.startsWith("data:");
      const isFlash = layer?.source === "flash";

      const showWm = isFlash && !isDataUrl && wmPreviewUrl;
      // ✅ GATE: watermark ONLY flash, and NEVER data: (paint)
      if (!isFlash || isDataUrl || !url) {
        setWmPreviewUrl(null); // IMPORTANT: clears old watermark when switching layers
        console.log("[wm-gate] off", {
          isFlash,
          isDataUrl,
          source: layer?.source,
        });
        return;
      }

      try {
        const wmLogoUrl = "/tt-watermark.png";
        const wm = await makeWatermarkedThumbWithLogo(url, wmLogoUrl);
        if (cancelled) return;
        setWmPreviewUrl(wm);
        console.log("[wm-gate] ON (flash)", { source: layer?.source });
      } catch (e) {
        console.warn("[wm] decal preview failed", e);
        setWmPreviewUrl(null);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [layer?.url, layer?.source]);

  // Keyboard: Cmd/Ctrl+Z
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (!meta) return;

      // don’t steal while typing
      const t = e.target;
      const typing =
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.isContentEditable);
      if (typing) return;

      if (e.key && e.key.toLowerCase() === "z") {
        e.preventDefault();
        if (canUndoTransform(layer.id)) undoTransform(layer.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [layer.id]);

  // flush drag updates
  const flush = () => {
    rafId.current = 0;
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    const eps = 0.0005;
    if (
      Math.abs(p.u - lastSent.current.u) > eps ||
      Math.abs(p.v - lastSent.current.v) > eps
    ) {
      lastSent.current = p;
      setLayerTransform(layer.id, { tx: p.u, ty: p.v, _dragging: true });
    }
  };
  const schedule = (u, v) => {
    pending.current = { u, v };
    if (!rafId.current) rafId.current = requestAnimationFrame(flush);
  };

  const updateFromEvent = (e) => {
    if (!hasImage) return;

    const pos = pointerToEditor(e);
    if (!pos) return;

    // where the pointer is in editor space
    let ex = clamp01(pos.x);
    let ey = clamp01(pos.y);

    // 🔹 apply stored offset so the part of the decal you grabbed stays under cursor
    if (dragOffsetRef.current) {
      ex = clamp01(ex + dragOffsetRef.current.dx);
      ey = clamp01(ey + dragOffsetRef.current.dy);
    }

    const u = clamp01(uvRect.x + ex * uvRect.w);
    const v = clamp01(uvRect.y + ey * uvRect.h);
    schedule(u, v);
  };

  // Convert pointer → canvas coords (after pan/zoom), same space as cover.{dx,dy,dw,dh}
  const getCoverPointFromEvent = (e) => {
    const wrap = stageRef.current;
    if (!wrap) return null;

    const r = wrap.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;

    // undo pan + zoom
    const cx = (px - pan.x) / zoom;
    const cy = (py - pan.y) / zoom;

    return { x: cx, y: cy };
  };

  const pointerToEditor = (e) => {
    const wrap = stageRef.current;
    if (!wrap) return null;

    const r = wrap.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;

    // undo pan + zoom
    const cx = (px - pan.x) / zoom;
    const cy = (py - pan.y) / zoom;

    let ex, ey;
    if (cover) {
      ex = (cx - cover.dx) / Math.max(1, cover.dw);
      ey = (cy - cover.dy) / Math.max(1, cover.dh);
    } else {
      ex = cx / Math.max(1, r.width);
      ey = cy / Math.max(1, r.height);
    }
    return { x: ex, y: ey };
  };

  const updateRotateFromEvent = (e) => {
    if (!hasImage) return;

    const drag = rotateDragRef.current;
    if (!drag) return; // we only rotate if a drag was started on the handle

    const pos = pointerToEditor(e);
    if (!pos) return;

    const ex = clamp01(pos.x);
    const ey = clamp01(pos.y);

    // center of the decal in editor space (0..1)
    const cx = posEd.x;
    const cy = posEd.y;

    // angle from center → pointer
    const a = Math.atan2(ey - cy, ex - cx);

    // target angle = pointer angle minus the offset we cached on pointer-down
    let target = a - drag.offset;

    // normalize toward [-PI, PI] so we don’t get big jumps when wrapping
    const PI2 = Math.PI * 2;
    while (target > Math.PI) target -= PI2;
    while (target < -Math.PI) target += PI2;

    // current rotation from state
    const current = T.rot || 0;

    // 🔹 smoothing: move only partway toward the target on each event
    const SMOOTH = 0.25; // lower = slower / less twitchy
    const next = current + (target - current) * SMOOTH;

    setLayerTransform(layer.id, {
      rot: next,
      _dragging: true,
    });
  };

  // Rotate world→local around center (so scale is axis-aligned in object space)
  const toLocalSpace = (pt, center, rotRad) => {
    const dx = pt.x - center.x;
    const dy = pt.y - center.y;
    const c = Math.cos(-rotRad);
    const s = Math.sin(-rotRad);
    return {
      x: dx * c - dy * s,
      y: dx * s + dy * c,
    };
  };

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const down = (e) => {
      if (!hasImage) return;

      // ✅ figure out whether we clicked the rotate handle
      const isRotateHandle =
        e.target && e.target.dataset && e.target.dataset.rotateHandle === "1";

      setRenderQuality("drag");
      console.log(
        "[DecalEditor] renderQuality NOW =",
        tattoo.renderQuality,
        tattoo.renderQualityTick
      );

      ensureUndoStart();
      draggingRef.current = true;
      el.setPointerCapture?.(e.pointerId);
      e.preventDefault();

      if (isRotateHandle) {
        modeRef.current = "rotate";

        // set up rotation offset so current angle stays as current rot
        const pos = pointerToEditor(e);
        if (pos) {
          const ex = clamp01(pos.x);
          const ey = clamp01(pos.y);
          const cx = posEd.x;
          const cy = posEd.y;
          const a0 = Math.atan2(ey - cy, ex - cx); // pointer angle at start
          const currentRot = T.rot || 0;
          rotateDragRef.current = { offset: a0 - currentRot };
        } else {
          rotateDragRef.current = { offset: 0 };
        }

        // ✅ IMPORTANT: do NOT rotate on pointerdown (prevents jump)
        // updateRotateFromEvent(e);  // ❌ remove
      } else {
        modeRef.current = "move";

        // compute offset between click point and decal center
        const pos = pointerToEditor(e);
        if (pos) {
          const ex = clamp01(pos.x);
          const ey = clamp01(pos.y);
          dragOffsetRef.current = {
            dx: posEd.x - ex,
            dy: posEd.y - ey,
          };
        } else {
          dragOffsetRef.current = null;
        }

        // ✅ also do NOT move on pointerdown (prevents jump)
        // updateFromEvent(e); // ❌ ensure this is NOT called
      }
    };

    const move = (e) => {
      if (!draggingRef.current) return;
      e.preventDefault();

      if (modeRef.current === "rotate") {
        updateRotateFromEvent(e);
      } else {
        updateFromEvent(e);
      }
    };

    const up = (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;

      try {
        el.releasePointerCapture?.(e.pointerId);
      } catch {}

      // ✅ flush pending position update (so last drag position applies)
      if (pending.current && !rafId.current) flush();

      dragOffsetRef.current = null;
      rotateDragRef.current = { offset: 0 };
      modeRef.current = null;

      setLayerTransform(layer.id, { _dragging: false });

      setRenderQuality("high");
      endInteraction();
    };

    const cancel = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;

      dragOffsetRef.current = null;
      rotateDragRef.current = { offset: 0 };
      modeRef.current = null;

      setLayerTransform(layer.id, { _dragging: false });

      setRenderQuality("high");
      endInteraction();
    };

    const blockCtx = (e) => e.preventDefault();

    el.addEventListener("pointerdown", down, { passive: false });
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel); // ✅ ADD
    el.addEventListener("lostpointercapture", cancel); // ✅ ADD (nice safety)
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("contextmenu", blockCtx);

    return () => {
      el.removeEventListener("pointerdown", down);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel); // ✅ ADD
      el.removeEventListener("lostpointercapture", cancel); // ✅ ADD
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("contextmenu", blockCtx);
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = 0;
      pending.current = null;
    };
  }, [
    layer.id,
    zoom,
    pan,
    cover,
    uvRect,
    hasImage,
    ensureUndoStart,
    endInteraction,
    posEd, // 🔹 add this
    T.rot, // safe if you want it super up to date
  ]);

  // 🔸 Nudge the 3D once so it picks up the current transform on initial mount/switch.
  useEffect(() => {
    const t = layer.transform || { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 };
    const baseScale = t.scale ?? 0.5;
    const sx = t.scaleX ?? baseScale;
    const sy = t.scaleY ?? baseScale;

    const id = requestAnimationFrame(() => {
      try {
        setLayerTransform(layer.id, {
          tx: t.tx ?? 0.5,
          ty: t.ty ?? 0.5,
          scaleX: sx,
          scaleY: sy,
          scale: baseScale,
          rot: t.rot ?? 0,
        });
      } catch {}
    });
    return () => cancelAnimationFrame(id);
  }, [layer.id]);

  // --- Nudge helpers (click + press-and-hold) ---------------
  // Keep a live ref of the current tx/ty so intervals don't use stale values
  const nudgePosRef = React.useRef({
    tx: T.tx ?? 0.5,
    ty: T.ty ?? 0.5,
  });

  React.useEffect(() => {
    nudgePosRef.current = {
      tx: T.tx ?? 0.5,
      ty: T.ty ?? 0.5,
    };
  }, [T.tx, T.ty, layer.id]);

  // core nudge math – no undo bookkeeping
  const applyNudge = (dx, dy) => {
    if (!hasImage) return;

    const stepU = (uvRect.w || 1) * NUDGE_STEP_FRACTION;
    const stepV = (uvRect.h || 1) * NUDGE_STEP_FRACTION;

    const curTx = nudgePosRef.current.tx;
    const curTy = nudgePosRef.current.ty;

    const nextU = clamp01(curTx + dx * stepU);
    const nextV = clamp01(curTy + dy * stepV);

    nudgePosRef.current = { tx: nextU, ty: nextV };
    setLayerTransform(layer.id, { tx: nextU, ty: nextV });
  };

  // single-step nudge (handy if we ever call it from keyboard etc.)
  const nudge = (dx, dy) => {
    if (!hasImage) return;
    ensureUndoStart();
    applyNudge(dx, dy);
    endInteraction();
  };

  // press & hold support
  const holdTimerRef = React.useRef(null);
  const holdDirRef = React.useRef(null);

  const clearHoldTimer = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const startHold = (dx, dy) => {
    if (!hasImage) return;

    // one undo span for the whole hold
    ensureUndoStart();

    // first nudge immediately
    applyNudge(dx, dy);
    holdDirRef.current = { dx, dy };

    clearHoldTimer();
    // after a short delay, start repeating in that direction
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = setInterval(() => {
        const dir = holdDirRef.current;
        if (!dir) return;
        applyNudge(dir.dx, dir.dy);
      }, 80); // repetition speed (ms)
    }, 220); // how long you must hold before it repeats
  };

  const stopHold = () => {
    clearHoldTimer();
    holdDirRef.current = null;
    endInteraction();
  };

  const makeHoldHandlers = (dx, dy) => ({
    onMouseDown: (e) => {
      e.preventDefault();
      startHold(dx, dy);
    },
    onMouseUp: stopHold,
    onMouseLeave: stopHold,
    onTouchStart: (e) => {
      e.preventDefault();
      startHold(dx, dy);
    },
    onTouchEnd: stopHold,
    onTouchCancel: stopHold,
  });

  // clean up on unmount / layer switch
  React.useEffect(() => {
    return () => {
      stopHold();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layer.id]);

  // --- styles ------------------------------------------------
  const stageStyle = {
    position: "relative",
    border: "1px solid #2b2b2b",
    borderRadius: 8,
    background: "#111",
    overflow: "hidden",
    cursor: hasImage ? (zoom > 1 ? "grab" : "crosshair") : "default",
    userSelect: "none",
    touchAction: "none",
    width: "100%",
    aspectRatio: "1 / 1",
  };
  const contentStyle = {
    position: "absolute",
    inset: 0,
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: "top left",
  };
  const dpadOuterStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    gap: 8,
    padding: 16,
    borderRadius: 16,
    border: "1px solid #3a3a3a",
    background: "#202020",
    minWidth: "260",
  };

  const dpadCellStyle = {
    borderRadius: 14,
    border: "1px solid #4a4a4a",
    background: "#2b2b2b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    cursor: "pointer",
    userSelect: "none",
  };

  // near the top of DecalEditor.jsx (module scope)
  const SCALE_ACTIVATE_PX = 3; // how far you must move before scaling kicks in

  const startCornerScaleDrag = (corner, e) => {
    if (!hasImage || !cover) return;

    e.preventDefault();
    e.stopPropagation();

    ensureUndoStart();

    // center of the decal in cover-space pixels
    const center = {
      x: cover.dx + posEd.x * cover.dw,
      y: cover.dy + posEd.y * cover.dh,
    };

    const startPt = getCoverPointFromEvent(e) || center;

    // local pos at mouse-down (this describes current half-size in local space)
    const local0 = toLocalSpace(startPt, center, T.rot || 0);

    // corner → sign
    const cornerSigns = {
      tl: { sx: -1, sy: -1 },
      tr: { sx: 1, sy: -1 },
      br: { sx: 1, sy: 1 },
      bl: { sx: -1, sy: 1 },
    };
    const signs = cornerSigns[corner] || { sx: 1, sy: 1 };

    // starting half-width/height in local space
    const startHalfW = Math.max(4, Math.abs(local0.x));
    const startHalfH = Math.max(4, Math.abs(local0.y));

    gizmoDragRef.current = {
      mode: "scale",
      corner,
      signs,
      center,
      startRot: T.rot || 0,
      startSx: clampedSx,
      startSy: clampedSy,
      startHalfW,
      startHalfH,
      startScreenPt: startPt,
      activated: false, // ⬅️ no scaling until we actually move a bit
    };

    const onMove = (ev) => {
      const drag = gizmoDragRef.current;
      if (!drag || drag.mode !== "scale") return;

      ev.preventDefault();

      const pt = getCoverPointFromEvent(ev);
      if (!pt) return;

      // 1) activation threshold (no pop on click)
      const dxScreen = pt.x - drag.startScreenPt.x;
      const dyScreen = pt.y - drag.startScreenPt.y;
      const moveDist = Math.sqrt(dxScreen * dxScreen + dyScreen * dyScreen);

      if (!drag.activated) {
        if (moveDist < SCALE_ACTIVATE_PX) {
          // still basically just a click → do nothing yet
          return;
        }
        drag.activated = true;
      }

      // 2) compute local coords relative to center, in rotated space
      const local = toLocalSpace(pt, drag.center, drag.startRot);

      // current half-width/height in local space
      const curHalfW = Math.max(4, Math.abs(local.x));
      const curHalfH = Math.max(4, Math.abs(local.y));

      // 3) scale relative to the *starting* half-size
      const factorX = curHalfW / drag.startHalfW;
      const factorY = curHalfH / drag.startHalfH;

      let nextSx = safeScale(drag.startSx * factorX);
      let nextSy = safeScale(drag.startSy * factorY);

      if (lockAspect) {
        // Preserve the sx:sy ratio captured at drag-start. Whichever axis
        // moved more drives the scale (so the user feels "follows my
        // finger"), and we apply that uniform factor to BOTH starting
        // dimensions — meaning a non-uniform decal stays non-uniform.
        // Previously this picked `max(nextSx, nextSy)` and assigned to
        // both, which snapped non-uniform decals back to 1:1.
        const uniformFactor = Math.max(factorX, factorY);
        nextSx = safeScale(drag.startSx * uniformFactor);
        nextSy = safeScale(drag.startSy * uniformFactor);
      }

      setLayerTransform(layer.id, {
        scaleX: nextSx,
        scaleY: nextSy,
        scale: (nextSx + nextSy) * 0.5, // keep legacy in sync
      });
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      gizmoDragRef.current = null;
      endInteraction();
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { once: true });
  };

  // --- gizmo geometry in cover-space pixels -------------------
  const gizmoCenterPx = cover
    ? {
        x: cover.dx + posEd.x * cover.dw,
        y: cover.dy + posEd.y * cover.dh,
      }
    : null;

  const halfW = cover ? (cover.dw * clampedSx) / 2 : 0;
  const halfH = cover ? (cover.dh * clampedSy) / 2 : 0;

  const I = ({ icon: Icon, ...props }) => (
    <Icon size={16} strokeWidth={2.25} aria-hidden="true" {...props} />
  );

  const iconBtnStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  };
  // --- render ------------------------------------------------
  return (
    <div
      className="decal-editor"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Zoom controls */}
      <div
        className="zoom-row"
        style={{
          gap: 8,
          marginBottom: 8,
          alignItems: "center",
        }}
      >
        <button className="btn-ghost" onClick={() => zoomTo(zoom / 1.25)}>
          <I icon={Minus} />
        </button>

        <button className="btn-ghost" onClick={() => resetView()}>
          {Math.round(zoom * 100)}%
        </button>

        <button className="btn-ghost" onClick={() => zoomTo(zoom * 1.25)}>
          <I icon={Plus} />
        </button>
        <button
          className="btn-ghost"
          onClick={() => undoTransform(layer.id)}
          disabled={!undoReady}
          title="Undo (⌘/Ctrl+Z)"
        >
          Undo
        </button>
      </div>
      <div className="zoom-tip" style={{ marginTop: 10, paddingBottom: 10 }}>
        <span className="tt-muted" style={{ fontSize: 12 }}>
          Tip: hold <strong>Ctrl/⌘</strong> + scroll to zoom. Right-drag to pan.
        </span>
      </div>
      {/* Stage */}
      <div ref={stageRef} style={stageStyle}>
        <div ref={contentRef} style={contentStyle}>
          {guideSrc && cover ? (
            <div
              style={{
                position: "absolute",
                left: `${cover.dx}px`,
                top: `${cover.dy}px`,
                width: `${cover.dw}px`,
                height: `${cover.dh}px`,
                pointerEvents: "none",
              }}
            >
              {/* UV guide exactly fills the cover box */}
              <img
                src={guideSrc}
                alt=""
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "fill",
                  opacity: 0.32,
                  pointerEvents: "none",
                }}
              />

              {/* Decal positioned in the same box (cover space) */}
              {hasImage && (
                <>
                  {/* Decal */}
                  <img
                    src={srcToShow}
                    alt={layer.name}
                    draggable={false}
                    style={{
                      position: "absolute",
                      left: `${posEd.x * 100}%`,
                      top: `${posEd.y * 100}%`,
                      transform: `translate(-50%, -50%) rotate(${rotDeg}deg) scale(${clampedSx}, ${clampedSy})`,
                      transformOrigin: "center center",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      pointerEvents: "none",
                      willChange: "transform",
                    }}
                  />

                  {/* 🔹 corner rotate handles — sit just outside each scale
                      node (diagonally offset from the corner). Replaces the
                      old single top-center rotate handle, which could end up
                      off-canvas when the decal sits near the top edge of the
                      stage. "grab" cursor (vs. the scale nodes' resize
                      cursors) hints that this is a rotate gesture. */}
                  {hasImage && cover && gizmoCenterPx && (
                    <>
                      {[
                        { id: "tl", dx: -1, dy: -1 },
                        { id: "tr", dx: 1, dy: -1 },
                        { id: "br", dx: 1, dy: 1 },
                        { id: "bl", dx: -1, dy: 1 },
                      ].map((c) => {
                        const ROTATE_OFFSET = 26; // px beyond the scale node
                        const cx = gizmoCenterPx.x + c.dx * (halfW + ROTATE_OFFSET);
                        const cy = gizmoCenterPx.y + c.dy * (halfH + ROTATE_OFFSET);

                        return (
                          <div
                            key={`rotate-${c.id}`}
                            data-rotate-handle="1"
                            style={{
                              position: "absolute",
                              left: `${cx}px`,
                              top: `${cy}px`,
                              transform: "translate(-50%, -50%)",
                              width: 32, // touch hit-box
                              height: 32,
                              borderRadius: "999px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "grab",
                              pointerEvents: "auto",
                              touchAction: "none",
                            }}
                            title="Drag to rotate"
                          >
                            <div
                              data-rotate-handle="1"
                              style={{
                                width: 20, // visible circle
                                height: 20,
                                borderRadius: "999px",
                                border: "2px solid #f5f5f5",
                                background: "rgba(0,0,0,0.8)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                boxShadow: "0 0 6px rgba(0,0,0,0.7)",
                                pointerEvents: "none", // events hit the outer hit-box
                              }}
                            >
                              ⟳
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                  {/* 🔹 corner handles (bigger hit areas for touch) */}
                  {hasImage && cover && gizmoCenterPx && (
                    <>
                      {[
                        { id: "tl", dx: -1, dy: -1 },
                        { id: "tr", dx: 1, dy: -1 },
                        { id: "br", dx: 1, dy: 1 },
                        { id: "bl", dx: -1, dy: 1 },
                      ].map((c) => {
                        const cx = gizmoCenterPx.x + c.dx * halfW;
                        const cy = gizmoCenterPx.y + c.dy * halfH;

                        return (
                          <div
                            key={c.id}
                            onPointerDown={(e) => startCornerScaleDrag(c.id, e)}
                            style={{
                              position: "absolute",
                              left: `${cx}px`,
                              top: `${cy}px`,
                              transform: "translate(-50%, -50%)",
                              width: 40, // big touch hit-box
                              height: 40,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "nwse-resize",
                              pointerEvents: "auto",
                              touchAction: "none",
                            }}
                          >
                            <div
                              style={{
                                width: 22, // visible square inside
                                height: 22,
                                borderRadius: 4,
                                border: "2px solid #f5f5f5",
                                background: "rgba(0,0,0,0.85)",
                                boxShadow: "0 0 5px rgba(0,0,0,0.7)",
                                pointerEvents: "none",
                              }}
                            />
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {!hasImage && (
                <div
                  className="tt-empty"
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    color: "#cfcfcf",
                    fontSize: 13,
                    pointerEvents: "none",
                  }}
                >
                  Add an image or paint to start editing this layer.
                </div>
              )}

              {!hasImage && (
                <div
                  className="tt-empty"
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "grid",
                    placeItems: "center",
                    color: "#cfcfcf",
                    fontSize: 13,
                    pointerEvents: "none",
                  }}
                >
                  Add an image or paint to start editing this layer.
                </div>
              )}
            </div>
          ) : (
            /* fallback while dims not ready */
            <>
              {guideSrc && (
                <img
                  src={guideSrc}
                  alt=""
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    opacity: 0.32,
                    pointerEvents: "none",
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Controls */}
      <div
        style={{
          display: "grid",
          gap: 8,
          marginTop: 10,
          opacity: hasImage ? 1 : 0.6,
        }}
      >
        {/* Scale / Free transform */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 11,
              opacity: 0.85,
            }}
          >
            <input
              type="checkbox"
              checked={lockAspect}
              onChange={(e) => setLockAspect(e.target.checked)}
              disabled={!hasImage}
            />
            Lock proportions
          </label>

          {/* Reset ratio — only surfaces when the current sx:sy is meaningfully
              non-uniform. Clicking it averages both axes to (sx+sy)/2 so the
              decal returns to the source image's 1:1 ratio at the same overall
              size. Works whether the lock is on or off; if on, subsequent
              scaling keeps the new (now uniform) ratio. */}
          {hasImage &&
            Math.abs(clampedSx - clampedSy) /
              Math.max(clampedSx, clampedSy, 0.0001) >
              0.01 && (
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  ensureUndoStart();
                  const avg = safeScale((clampedSx + clampedSy) * 0.5);
                  setLayerTransform(layer.id, {
                    scaleX: avg,
                    scaleY: avg,
                    scale: avg,
                  });
                  endInteraction();
                }}
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  opacity: 0.85,
                }}
                title="Restore the source image's original 1:1 aspect ratio"
              >
                Reset ratio
              </button>
            )}

          {lockAspect ? (
            // Uniform scaling
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap", // ✅ allow wrap on small widths
                minWidth: 0,
              }}
            >
              <span style={{ width: 20 }}>
                <I icon={Scaling} />
              </span>

              {/* minus button */}
              {/* minus button */}
              <button
                type="button"
                className="btn-ghost"
                disabled={!hasImage}
                onClick={() => {
                  if (!hasImage) return;
                  ensureUndoStart();

                  // Proportional shrink — multiply BOTH axes by 0.97 so the
                  // current sx:sy ratio is preserved. (Was: snap both axes
                  // to a single uniform value, which destroyed the ratio.)
                  const k = 0.97;
                  const nextSx = safeScale(clampedSx * k);
                  const nextSy = safeScale(clampedSy * k);

                  setLayerTransform(layer.id, {
                    scaleX: nextSx,
                    scaleY: nextSy,
                    scale: (nextSx + nextSy) * 0.5,
                    _dragging: true,
                  });

                  endInteraction();
                }}
                style={{ width: 28 }}
              >
                −
              </button>

              <input
                type="range"
                min={0}
                max={1}
                step={0.001}
                value={uniformSliderVal}
                onPointerDown={ensureUndoStart}
                onPointerUp={endInteraction}
                onPointerCancel={endInteraction}
                onChange={(e) => {
                  // Locked-aspect slider grows/shrinks the whole decal while
                  // preserving the current sx:sy ratio. The slider's "value"
                  // is uniformScale = (sx + sy) / 2, so a slider move from
                  // `currentUni` to `s` corresponds to a uniform factor of
                  // `s / currentUni` applied to both axes.
                  const s = sliderToScale(parseFloat(e.target.value));
                  const currentUni = (clampedSx + clampedSy) * 0.5;
                  if (currentUni <= 0) {
                    // Edge case: zero-scale layer. Treat as a fresh uniform start.
                    setLayerTransform(layer.id, { scaleX: s, scaleY: s, scale: s });
                    return;
                  }
                  const factor = s / currentUni;
                  const nextSx = safeScale(clampedSx * factor);
                  const nextSy = safeScale(clampedSy * factor);
                  setLayerTransform(layer.id, {
                    scaleX: nextSx,
                    scaleY: nextSy,
                    scale: (nextSx + nextSy) * 0.5,
                  });
                }}
                style={{ flex: 1 }}
                disabled={!hasImage}
              />

              {/* plus button */}
              <button
                type="button"
                className="btn-ghost"
                disabled={!hasImage}
                onClick={() => {
                  if (!hasImage) return;
                  ensureUndoStart();
                  // Proportional grow — see notes on the matching minus
                  // button above.
                  const k = 1.03;
                  const nextSx = safeScale(clampedSx * k);
                  const nextSy = safeScale(clampedSy * k);
                  setLayerTransform(layer.id, {
                    scaleX: nextSx,
                    scaleY: nextSy,
                    scale: (nextSx + nextSy) * 0.5,
                  });
                  endInteraction();
                }}
                style={{ width: 28 }}
              >
                +
              </button>

              <span style={{ width: 52, textAlign: "right" }}>
                {Math.round(uniformScale * 100)}%
              </span>
            </label>
          ) : (
            // Free transform: independent X / Y scaling
            <>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span style={{ width: 40 }}>
                  <I icon={Scaling} />
                </span>

                <button
                  type="button"
                  className="btn-ghost"
                  disabled={!hasImage}
                  onClick={() => {
                    if (!hasImage) return;
                    ensureUndoStart();
                    const nextSlider = Math.max(0, scaleXSliderVal - 0.01);
                    const s = sliderToScale(nextSlider);
                    setLayerTransform(layer.id, {
                      scaleX: s,
                      scaleY: T.scaleY ?? baseScale,
                      scale: (s + (T.scaleY ?? baseScale)) * 0.5,
                    });
                    endInteraction();
                  }}
                >
                  –
                </button>

                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.001}
                  value={scaleXSliderVal}
                  onPointerDown={ensureUndoStart}
                  onPointerUp={endInteraction}
                  onPointerCancel={endInteraction}
                  onChange={(e) => {
                    const s = sliderToScale(parseFloat(e.target.value));
                    setLayerTransform(layer.id, {
                      scaleX: s,
                      scaleY: T.scaleY ?? baseScale,
                      scale: (s + (T.scaleY ?? baseScale)) * 0.5,
                    });
                  }}
                  style={{ flex: 1 }}
                  disabled={!hasImage}
                />

                <button
                  type="button"
                  className="btn-ghost"
                  disabled={!hasImage}
                  onClick={() => {
                    if (!hasImage) return;
                    ensureUndoStart();
                    const nextSlider = Math.min(1, scaleXSliderVal + 0.01);
                    const s = sliderToScale(nextSlider);
                    setLayerTransform(layer.id, {
                      scaleX: s,
                      scaleY: T.scaleY ?? baseScale,
                      scale: (s + (T.scaleY ?? baseScale)) * 0.5,
                    });
                    endInteraction();
                  }}
                >
                  +
                </button>

                <span style={{ width: 52, textAlign: "right" }}>
                  {Math.round(clampedSx * 100)}%
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span style={{ width: 70 }}>Height</span>

                <button
                  type="button"
                  className="btn-ghost"
                  disabled={!hasImage}
                  onClick={() => {
                    if (!hasImage) return;
                    ensureUndoStart();
                    const nextSlider = Math.max(0, scaleYSliderVal - 0.01);
                    const s = sliderToScale(nextSlider);
                    setLayerTransform(layer.id, {
                      scaleX: T.scaleX ?? baseScale,
                      scaleY: s,
                      scale: ((T.scaleX ?? baseScale) + s) * 0.5,
                    });
                    endInteraction();
                  }}
                >
                  –
                </button>

                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.001}
                  value={scaleYSliderVal}
                  onPointerDown={ensureUndoStart}
                  onPointerUp={endInteraction}
                  onPointerCancel={endInteraction}
                  onChange={(e) => {
                    const s = sliderToScale(parseFloat(e.target.value));
                    setLayerTransform(layer.id, {
                      scaleX: T.scaleX ?? baseScale,
                      scaleY: s,
                      scale: ((T.scaleX ?? baseScale) + s) * 0.5,
                    });
                  }}
                  style={{ flex: 1 }}
                  disabled={!hasImage}
                />

                <button
                  type="button"
                  className="btn-ghost"
                  disabled={!hasImage}
                  onClick={() => {
                    if (!hasImage) return;
                    ensureUndoStart();
                    const nextSlider = Math.min(1, scaleYSliderVal + 0.01);
                    const s = sliderToScale(nextSlider);
                    setLayerTransform(layer.id, {
                      scaleX: T.scaleX ?? baseScale,
                      scaleY: s,
                      scale: ((T.scaleX ?? baseScale) + s) * 0.5,
                    });
                    endInteraction();
                  }}
                >
                  +
                </button>

                <span style={{ width: 52, textAlign: "right" }}>
                  {Math.round(clampedSy * 100)}%
                </span>
              </label>
            </>
          )}
        </div>

        {/* Rotate with step buttons */}
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
        >
          <span style={{ width: 20 }}>
            <I icon={RotateCcw} />
          </span>

          {/* -1° button */}
          <button
            type="button"
            className="btn-ghost"
            disabled={!hasImage}
            onClick={() => {
              if (!hasImage) return;
              ensureUndoStart();
              const nextDeg = rotDeg - 1;
              setLayerTransform(layer.id, {
                rot: toRad(nextDeg),
              });
              endInteraction();
            }}
            style={{ width: 28 }}
          >
            −
          </button>

          <input
            type="range"
            min={-180}
            max={180}
            step={1}
            value={rotDeg}
            onPointerDown={ensureUndoStart}
            onPointerUp={endInteraction}
            onPointerCancel={endInteraction}
            onChange={(e) =>
              setLayerTransform(layer.id, {
                rot: toRad(parseFloat(e.target.value)),
              })
            }
            style={{ flex: 1 }}
            disabled={!hasImage}
          />

          {/* +1° button */}
          <button
            type="button"
            className="btn-ghost"
            disabled={!hasImage}
            onClick={() => {
              if (!hasImage) return;
              ensureUndoStart();
              const nextDeg = rotDeg + 1;
              setLayerTransform(layer.id, {
                rot: toRad(nextDeg),
              });
              endInteraction();
            }}
            style={{ width: 28 }}
          >
            +
          </button>

          <input
            type="number"
            min={-180}
            max={180}
            step={1}
            value={rotDeg}
            onFocus={ensureUndoStart}
            onBlur={endInteraction}
            onChange={(e) =>
              setLayerTransform(layer.id, {
                rot: toRad(
                  Math.max(-180, Math.min(180, parseFloat(e.target.value) || 0))
                ),
              })
            }
            style={{ width: 50 }}
            disabled={!hasImage}
          />
          <button
            className="btn-ghost"
            onClick={() => {
              ensureUndoStart();
              setLayerTransform(layer.id, { rot: 0 });
              endInteraction();
            }}
            disabled={!hasImage}
          >
            0°
          </button>
        </label>

        {/* Nudge controls – keep your existing block here unchanged */}
        {/* ... your D-pad block exactly as you have it now ... */}

        {/* Nudge controls */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
          }}
        >
          {/* Outer D-pad frame */}
          <div
            style={{
              padding: 8,
              borderRadius: 8,
              border: "1px solid #3a3a3a",
              background: "#252525",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100%",
            }}
          >
            {/* 3×2 grid: left & right are double-height */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                columnGap: 12,
                rowGap: 12,
                width: "100%",
                maxWidth: 320,
              }}
            >
              {/* left (double height) */}
              <button
                type="button"
                className="btn-ghost"
                {...makeHoldHandlers(-1, 0)}
                disabled={!hasImage}
                title="Nudge left"
                style={{
                  gridColumn: 1,
                  gridRow: "1 / span 2",
                  borderRadius: 14,
                  border: "1px solid #4a4a4a",
                  background: "#303030",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "100%",
                }}
              >
                ◀
              </button>

              {/* up (top-middle) */}
              <button
                type="button"
                className="btn-ghost"
                {...makeHoldHandlers(0, -1)}
                disabled={!hasImage}
                title="Nudge up"
                style={{
                  gridColumn: 2,
                  gridRow: 1,
                  borderRadius: 14,
                  border: "1px solid #4a4a4a",
                  background: "#303030",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 56,
                }}
              >
                ▲
              </button>

              {/* down (bottom-middle) */}
              <button
                type="button"
                className="btn-ghost"
                {...makeHoldHandlers(0, 1)}
                disabled={!hasImage}
                title="Nudge down"
                style={{
                  gridColumn: 2,
                  gridRow: 2,
                  borderRadius: 14,
                  border: "1px solid #4a4a4a",
                  background: "#303030",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 56,
                }}
              >
                ▼
              </button>

              {/* right (double height) */}
              <button
                type="button"
                className="btn-ghost"
                {...makeHoldHandlers(1, 0)}
                disabled={!hasImage}
                title="Nudge right"
                style={{
                  gridColumn: 3,
                  gridRow: "1 / span 2",
                  borderRadius: 14,
                  border: "1px solid #4a4a4a",
                  background: "#303030",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "100%",
                }}
              >
                ▶
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
